import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TopNav,
  IconButton,
  Icon,
  icons,
  Avatar,
  EmptyState,
  Button,
  theme,
} from '@agentisai/design-system';
import {
  MAX_TEXT_LEN,
  type Attachment,
  type TurnArtifact,
  type WorkstreamMode,
} from '@agentisai/shared-types';
import { useAuth } from '../auth/AuthProvider';
import { CreateWorkstreamPanel } from '../components/CreateWorkstreamPanel';
import { UserMenu } from '../components/UserMenu';
import { WorkspaceShell } from '../components/WorkspaceShell';
import { WorkspaceConversation } from '../components/WorkspaceConversation';
import { WorkstreamArtifacts } from '../components/WorkstreamArtifacts';
import type { DisplayArtifact } from '../components/ArtifactPreview';
import { useViewportTier } from '../hooks/useViewportTier';
import {
  NewWorkstreamAction,
  WorkstreamsRailBody,
  WS_PARAM,
} from '../components/WorkstreamsRailBody';
import { useWorkstreams } from '../queries/useWorkstreams';
import { sendChatTurn, createWorkstream, runWorkstreamChatTurn } from '../queries/client';
import { keys } from '../queries/keys';
import { describeError } from '../lib/describeError';
import { formatMessageTime } from '../lib/formatMessageTime';

/**
 * A single turn in the client-held transcript. `role` + `content` are the
 * API wire shape (posted as `messages[]` on every chat turn and on create);
 * `createdAt` is the server-authoritative RFC-3339 instant, stored RAW and
 * formatted at render (frontend-architecture R2) — never a prebaked string.
 */
type TurnEntry = {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
};

/**
 * Presigned artifact URLs die with the Lambda role session (~1 h, ADR-0040
 * ceiling). 55 min leaves margin so a rendered link doesn't die mid-click;
 * past it the panel shows the honest "ask the agent to re-share" state.
 */
const PRESIGNED_URL_TTL_MS = 55 * 60 * 1000;

function errorMessage(err: unknown): string {
  // Friendly, specific copy per error class (validation / server / network),
  // not a blanket "Something went wrong" — see lib/describeError.
  return describeError(err).userMessage;
}

/**
 * WorkstreamSession — ONE chat's worth of state (transcript, pending id,
 * ledger, mode…). HomePage remounts it via `key` when the active chat
 * changes, so every per-chat state slot resets at once — no manual reset
 * list to drift out of date as slots are added.
 *
 * S-F1: renders the first-login EMPTY STATE for a fresh tenant, replicating
 * the `Genesis / Sample Workspaces / StratBridge_Daniel` DS story in SHAPE,
 * brand-swapped to the signed-in tenant (BG Private / Jaco for Drop 1).
 *
 * S14 wired the "+ Create First Workstream" CTA to open the modal.
 * S15 built CreateWorkstreamPanel (ChatPanel fluid + seeded intro + Composer).
 * S16 wires the two useMutations — chat turn + explicit create — so the panel
 * is a live conversational round-trip against the S05 canned-reply handlers.
 */
function WorkstreamSession({
  initialWorkstreamId,
  autoOpenCreate,
  onNew,
  onCreated,
}: {
  /** Rail-selected workstream to open into (null = fresh draft chat). */
  readonly initialWorkstreamId: string | null;
  /** Open the create modal on mount ("+ New" remounts into an open modal). */
  readonly autoOpenCreate: boolean;
  /** Start a fresh draft chat (HomePage remounts this component). */
  readonly onNew: () => void;
  /** Reports the created workstreamId up so HomePage can sync the URL. */
  readonly onCreated: (id: string) => void;
}) {
  const { user, tenantBrand, getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(autoOpenCreate);

  // Drives the main-pane empty-state copy + CTA. Only the loaded-and-empty
  // case shows "Create First Workstream"; once any workstream exists the
  // main pane prompts the user to pick one from the rail instead (opening
  // a workstream into a real workspace is deferred to a later drop).
  const workstreamsQuery = useWorkstreams();
  const hasWorkstreams = (workstreamsQuery.data?.data.length ?? 0) > 0;

  // Client-held transcript — the chat-companion model. Posted as `messages[]`
  // on every chat turn and as the final body on create. Server is stateless.
  const [transcript, setTranscript] = useState<TurnEntry[]>([]);
  const [composerValue, setComposerValue] = useState('');
  const [workstreamId, setWorkstreamId] = useState<string | null>(initialWorkstreamId);

  // Client-minted id for the workstream-to-be — stable across every pre-create
  // chat turn AND the eventual create, so the agent pipeline accumulates
  // discovery state (mode, summary, PRD artifact ledger) under ONE id instead
  // of minting a throwaway per turn (the bug: three turns, three ids, no state).
  const [pendingWorkstreamId] = useState(() => crypto.randomUUID());

  // Agent-proposed workstream identity (ADR-0044, additive-optional
  // `data.workstreamTitle`/`workstreamDescription` on the pre-create chat
  // response). First arrival triggers the auto-create below; held so a manual
  // "Create workstream" click after a failed auto-create still carries it.
  const [wsIdentity, setWsIdentity] = useState<{ title: string; description?: string } | null>(
    null,
  );

  // Post-creation composer draft + invite-agent selection — reset per workstream.
  const [conversationValue, setConversationValue] = useState('');
  const [invitedAgent, setInvitedAgent] = useState<string | null>(null);

  // PDF staged for the next turn (turn-scoped — sent once, then cleared).
  const [attachment, setAttachment] = useState<Attachment | null>(null);

  // Librarian routing mode, as last reported by the chat endpoint (additive-
  // optional `data.mode`). Server-owned; this is just the latest observation.
  const [wsMode, setWsMode] = useState<WorkstreamMode>('normal');

  // Consensus-tracker conflict detail for the latest turn (additive-optional
  // `data.consensus`/`data.conflictDetail`); null once a later turn agrees.
  const [conflict, setConflict] = useState<string | null>(null);

  // Agents that produced the latest reply, in execution order (additive-
  // optional `data.agents`) — rendered as the conversation status indicator.
  const [lastAgents, setLastAgents] = useState<string[]>([]);

  // The workstream's cross-turn artifact ledger (ADR-0041, additive-optional
  // `result.ledger`) as last reported — metadata only on the wire (url/preview
  // stripped server-side; the S3 key is the durable identity). Feeds the
  // persistent WorkstreamArtifacts panel (Stage 3c), so deliverables no
  // longer vanish when the next turn's result replaces the turn artifacts.
  const [ledger, setLedger] = useState<TurnArtifact[]>([]);

  // Full artifacts (url + preview) from every turn that wrote them, keyed by
  // S3 key and accumulated across turns — the panel's only content source,
  // since the ledger is metadata-only and no re-fetch endpoint exists yet
  // (a durable download path is Stage 4 authz territory, ADR-0040).
  // `receivedAt` anchors the presigned-URL expiry computed at render.
  const [heldArtifacts, setHeldArtifacts] = useState<
    Record<string, TurnArtifact & { receivedAt: number }>
  >({});

  // The seeded intro bubble's timestamp — stamped at mount (every fresh chat
  // is a fresh mount), so it reads "Just now" on open and ages with the
  // transcript.
  const [introCreatedAt] = useState(() => new Date().toISOString());

  // Coarse wall-clock tick (every 30s) fed into the render-time formatter so
  // "Just now" flips to a clock without per-second ticking. The sanctioned
  // external-system-sync effect (frontend-architecture R24/R25) — NOT
  // data-in-effect; one interval re-renders every bubble's time at once.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const sendMutation = useMutation({
    mutationKey: keys.workstream.createChat(),
    mutationFn: ({
      question,
      questionCreatedAt,
    }: {
      question: string;
      questionCreatedAt: string;
    }) => {
      const apiMessages = transcript.map(({ role, content }) => ({ role, content }));
      return sendChatTurn(
        { messages: apiMessages, question, questionCreatedAt, workstreamId: pendingWorkstreamId },
        { getAccessToken },
      );
    },
    onSuccess: (data) => {
      // Render from the SERVER's authoritative timestamps (the echoed user turn
      // + the reply), not the local clock. Fall back defensively if an older
      // server omits them.
      setTranscript((prev) => [
        ...prev,
        {
          role: 'user',
          content: data.data.userMessage.content,
          createdAt: data.data.userMessage.createdAt ?? new Date().toISOString(),
        },
        {
          role: 'assistant',
          content: data.data.reply.content,
          createdAt: data.data.reply.createdAt ?? new Date().toISOString(),
        },
      ]);
      setComposerValue('');
      // Auto-create (ADR-0044): the first turn that arrives carrying an
      // agent-proposed title means the pipeline understood the ask — persist
      // the workstream now instead of waiting for a manual click. Messages are
      // passed explicitly (the setTranscript above hasn't flushed yet). A
      // failed auto-create surfaces createError in the modal and retries on
      // the next titled turn; the button stays as the manual fallback.
      const title = data.data.workstreamTitle;
      const description = data.data.workstreamDescription;
      if (title) setWsIdentity({ title, ...(description ? { description } : {}) });
      if (title && !workstreamId && !createMutation.isPending) {
        createMutation.mutate({
          messages: [
            ...transcript.map(({ role, content }) => ({ role, content })),
            { role: 'user', content: data.data.userMessage.content },
            { role: 'assistant', content: data.data.reply.content },
          ],
          title,
          ...(description ? { description } : {}),
        });
      }
    },
  });

  const createMutation = useMutation({
    mutationKey: keys.workstream.create(),
    // `void` (not an optional param) so the manual button's bare `mutate()`
    // stays legal; the auto-create path passes the fresh turn pair + identity
    // explicitly because its setTranscript hasn't flushed when it fires.
    mutationFn: (
      vars: {
        messages?: { role: 'user' | 'assistant'; content: string }[];
        title?: string;
        description?: string;
      } | void,
    ) => {
      const apiMessages =
        vars?.messages ?? transcript.map(({ role, content }) => ({ role, content }));
      const identity = vars?.title
        ? { title: vars.title, ...(vars.description ? { description: vars.description } : {}) }
        : wsIdentity
          ? {
              title: wsIdentity.title,
              ...(wsIdentity.description ? { description: wsIdentity.description } : {}),
            }
          : {};
      return createWorkstream(
        { messages: apiMessages, workstreamId: pendingWorkstreamId, ...identity },
        { getAccessToken },
      );
    },
    onSuccess: (data) => {
      setTranscript((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.data.reply.content,
          // The confirmation bubble's own createdAt, else the workstream instant.
          createdAt: data.data.reply.createdAt ?? data.data.createdAt,
        },
      ]);
      setWorkstreamId(data.data.workstreamId);
      // Sync the URL (?ws=) so the rail highlights the new workstream. HomePage
      // pins the key while ?ws matches the session-created id, so no remount.
      onCreated(data.data.workstreamId);
      // S-C1: invalidate the rail's list so the new workstream appears without
      // a reload. void — invalidation is fire-and-forget; the next render of a
      // useWorkstreams() consumer triggers the refetch.
      void queryClient.invalidateQueries({ queryKey: keys.workstream.listMine() });
      // The conversation continues in the main pane, not the modal.
      setCreateOpen(false);
    },
  });

  // Post-creation chat turn — same client-held-transcript pattern as the
  // create flow, scoped to the now-real workstreamId + the implicit
  // 'default' focus (contract-correct; no focus persistence yet).
  const conversationMutation = useMutation({
    mutationKey: workstreamId ? keys.workstream.chat(workstreamId) : ['workstream', 'chat', 'none'],
    mutationFn: ({
      question,
      questionCreatedAt,
      action,
    }: {
      question: string;
      questionCreatedAt: string;
      action?: 'chat' | 'exitDiscovery';
    }) => {
      if (!workstreamId) throw new Error('no active workstream');
      const apiMessages = transcript.map(({ role, content }) => ({ role, content }));
      // Async turn (ADR-0037): submit + poll inside one mutation — isPending
      // spans the whole turn, so the in-progress chrome needs no new state.
      return runWorkstreamChatTurn(
        {
          workstreamId,
          focusId: 'default',
          messages: apiMessages,
          question,
          questionCreatedAt,
          ...(invitedAgent ? { agentName: invitedAgent } : {}),
          ...(action ? { action } : {}),
          ...(attachment ? { attachment } : {}),
        },
        { getAccessToken },
      );
    },
    onSuccess: ({ userMessage, result }) => {
      setTranscript((prev) => [
        ...prev,
        {
          role: 'user',
          content: userMessage.content,
          createdAt: userMessage.createdAt ?? new Date().toISOString(),
        },
        {
          role: 'assistant',
          content: result.reply.content,
          createdAt: result.reply.createdAt ?? new Date().toISOString(),
        },
      ]);
      // Track the Librarian's routing mode when reported (gates the invite row
      // vs the exit-discovery affordance). Absent on older/stubbed servers.
      if (result.mode) setWsMode(result.mode);
      setLastAgents(result.agents ?? []);
      // Merge (never replace) this turn's full artifacts into the held cache,
      // and adopt the server's ledger when reported (absent leaves the last
      // known ledger standing — clearing it re-created the vanishing bug).
      const turnArtifacts = result.artifacts;
      if (turnArtifacts && turnArtifacts.length > 0) {
        const receivedAt = Date.now();
        setHeldArtifacts((prev) => ({
          ...prev,
          ...Object.fromEntries(turnArtifacts.map((a) => [a.key, { ...a, receivedAt }])),
        }));
      }
      if (result.ledger) setLedger(result.ledger);
      // Surface (or clear) the consensus conflict banner for this turn.
      setConflict(
        result.consensus === 'conflicted'
          ? (result.conflictDetail ?? 'The invited agents gave conflicting answers.')
          : null,
      );
      setConversationValue('');
      setAttachment(null);
    },
  });

  // Map the client-held transcript → DS Message objects for the panel renderer.
  const dsMessages = transcript.map((m, i) => ({
    id: `turn-${i}`,
    memberId: m.role === 'user' ? 'user' : 'workstream-agent',
    memberName: m.role === 'user' ? (user?.name ?? 'You') : 'Workstream Agent',
    memberRole: '',
    memberType: m.role === 'user' ? ('human' as const) : ('agent' as const),
    // Workstream Agent replies use the `mira` variant (purple ring + Stamp
    // badge) — the DS canonical for the create-flow agent, matching the seeded
    // intro (CreateWorkstreamPanel) and the DS ChatPanel "Create new workstream"
    // story. NOT `agent` (blue ring, no badge), which mis-branded every reply.
    variant: m.role === 'user' ? ('human' as const) : ('mira' as const),
    text: m.content,
    time: formatMessageTime(m.createdAt, now),
  }));

  const hasUserMessage = transcript.some((m) => m.role === 'user');
  // createDisabled: no user message yet, or create is in-flight, or already created.
  const createDisabled = !hasUserMessage || createMutation.isPending || workstreamId !== null;

  const sendError = sendMutation.error ? errorMessage(sendMutation.error) : null;
  const createError = createMutation.error ? errorMessage(createMutation.error) : null;
  const conversationError = conversationMutation.error
    ? errorMessage(conversationMutation.error)
    : null;

  // Panel list, derived at render (R2): ledger order, plus any held artifact
  // the ledger hasn't reported (an older server, or a ledger write that
  // lagged the turn) — each enriched with held url/preview by key, expiry
  // computed against the existing 30 s clock tick.
  const ledgerKeys = new Set(ledger.map((a) => a.key));
  const displayArtifacts: DisplayArtifact[] = [
    ...ledger,
    ...Object.values(heldArtifacts)
      .filter((a) => !ledgerKeys.has(a.key))
      .sort((a, b) => a.receivedAt - b.receivedAt),
  ].map((a) => {
    const held = heldArtifacts[a.key];
    return {
      ...a,
      url: held?.url,
      preview: a.preview ?? held?.preview,
      expired: held?.url != null && now - held.receivedAt > PRESIGNED_URL_TTL_MS,
    };
  });

  // The artifact panel renders beside the conversation only at the regular
  // tier — the DS prototype shell recipe (ArtifactWorkspace centre pane,
  // ChatPanel 40%/min-360 right). ponytail: compact tier keeps the full-pane
  // conversation and hides the panel; a compact artifact view is the R22
  // bottom-tab pattern, add when compact demand shows.
  const tier = useViewportTier();
  const showArtifactPanel =
    workstreamId !== null && tier === 'regular' && displayArtifacts.length > 0;

  // Derived, not stored (R2) — looked up from the already-cached rail list;
  // falls back before the S-C1 invalidation's refetch lands post-create.
  const activeTitle =
    workstreamsQuery.data?.data.find((w) => w.workstreamId === workstreamId)?.title ?? 'Workstream';

  // firstName from the JWT `name` claim. No useMemo — the React Compiler
  // memoizes (R11/R2). trim() before split so a leading space can't yield an
  // empty first token (mirrors the DS Avatar's own initials derivation).
  const firstName = user?.name?.trim().split(/\s+/)[0] ?? 'there';
  const fullName = user?.name ?? 'User';

  // Brand tint is DATA from tenantBrand (the bgprivate map → '#1e40af'), not a
  // hardcoded style token. Undefined-safe for any not-yet-mapped tenant.
  const tint = tenantBrand?.avatarTint;
  const brandName = tenantBrand?.displayName;

  const trailing = (
    <>
      <IconButton
        icon={<Icon as={icons.Bell} />}
        aria-label="Notifications"
        size="sm"
        variant="ghost"
      />
      {/* The working profile menu / Sign Out (S-H2) — replaces the bare
          trailing avatar. The EmptyState's display avatar below is
          unchanged. UserMenu reads its own claims via useAuth(). */}
      <UserMenu />
    </>
  );

  return (
    <div
      style={{
        // height (not minHeight): a definite viewport height so the RailPanel's
        // height:100% chain resolves and its content scrolls internally instead
        // of the whole page scrolling. overflow:hidden clips the shell to the
        // viewport; each inner pane owns its own scroll.
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: theme.color.neutral.bgSurface,
      }}
    >
      <TopNav trailing={trailing} />
      <WorkspaceShell
        workstreamsPanel={<WorkstreamsRailBody onNew={onNew} />}
        workstreamsAction={<NewWorkstreamAction onClick={onNew} />}
      >
        {workstreamId ? (
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            {showArtifactPanel && <WorkstreamArtifacts artifacts={displayArtifacts} />}
            <WorkspaceConversation
              title={activeTitle}
              fluid={!showArtifactPanel}
              messages={dsMessages}
              value={conversationValue}
              onChange={setConversationValue}
              onSend={(q) =>
                conversationMutation.mutate({
                  question: q,
                  questionCreatedAt: new Date().toISOString(),
                })
              }
              sendDisabled={
                conversationMutation.isPending ||
                conversationValue.length > MAX_TEXT_LEN ||
                undefined
              }
              maxLength={MAX_TEXT_LEN}
              pending={conversationMutation.isPending}
              sendError={conversationError}
              invitedAgent={invitedAgent}
              onInviteAgent={setInvitedAgent}
              mode={wsMode}
              activeAgents={lastAgents}
              conflict={conflict}
              attachment={attachment}
              onAttachmentChange={setAttachment}
              onExitDiscovery={() =>
                conversationMutation.mutate({
                  question: 'exit discovery',
                  questionCreatedAt: new Date().toISOString(),
                  action: 'exitDiscovery',
                })
              }
            />
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: theme.space[6],
            }}
          >
            <EmptyState
              icon={
                <Avatar
                  name={fullName}
                  size="display"
                  style={tint ? { background: tint, boxShadow: `0 0 0 2px ${tint}` } : undefined}
                />
              }
              title={
                <>
                  {brandName && (
                    <span
                      style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.caption,
                        fontWeight: theme.typography.fontWeight.semibold,
                        letterSpacing: theme.typography.letterSpacing.label,
                        textTransform: 'uppercase',
                        color: tint,
                        marginBottom: theme.space['1_5'],
                      }}
                    >
                      {brandName}
                    </span>
                  )}
                  Welcome to Genesis, {firstName}.
                </>
              }
              description={
                hasWorkstreams
                  ? `You're set up for ${
                      brandName ?? 'Genesis'
                    }. Select a workstream from the left to pick up where you left off.`
                  : `You're set up for ${
                      brandName ?? 'Genesis'
                    }. When you create your first workstream, it'll appear here. Or one of your colleagues will share theirs with you.`
              }
              action={
                // Show the "Create First Workstream" CTA only when we KNOW the
                // list is empty — hide it while loading (avoid a CTA flash) and
                // once any workstream exists (the rail + `+ New` action are the
                // create affordance from then on).
                workstreamsQuery.isSuccess && !hasWorkstreams ? (
                  // Matches the StratBridge_Daniel story's `EmptyStateCta` exactly:
                  // ghostStrong + 10x24 padding, body-coloured text, fainter border,
                  // 13px / 600 (bodySm / semibold) — not the default Button metrics.
                  <Button
                    variant="ghostStrong"
                    style={{
                      padding: '10px 24px',
                      color: theme.color.neutral.body,
                      borderColor: theme.color.neutral.fainter,
                      fontSize: theme.typography.fontSize.bodySm,
                      fontWeight: theme.typography.fontWeight.semibold,
                    }}
                    onClick={() => setCreateOpen(true)}
                  >
                    + Create First Workstream
                  </Button>
                ) : undefined
              }
            />
          </div>
        )}
      </WorkspaceShell>
      <CreateWorkstreamPanel
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        messages={dsMessages}
        introTime={formatMessageTime(introCreatedAt, now)}
        value={composerValue}
        onChange={setComposerValue}
        onSend={(q) =>
          sendMutation.mutate({ question: q, questionCreatedAt: new Date().toISOString() })
        }
        // Block send while a turn is in flight OR the draft exceeds the shared
        // contract limit (MAX_TEXT_LEN) — the panel shows inline feedback so the
        // user knows BEFORE clicking, instead of a post-send server rejection.
        // `|| undefined` so the Composer's own empty-draft guard still applies.
        sendDisabled={sendMutation.isPending || composerValue.length > MAX_TEXT_LEN || undefined}
        maxLength={MAX_TEXT_LEN}
        pending={sendMutation.isPending}
        onCreate={() => createMutation.mutate()}
        createDisabled={createDisabled}
        creating={createMutation.isPending}
        sendError={sendError}
        createError={createError}
      />
    </div>
  );
}
WorkstreamSession.displayName = 'WorkstreamSession';

/**
 * HomePage — the protected '/' home (behind RequireAuth + RequireTenant).
 *
 * Owns WHICH chat is active; all per-chat state lives in WorkstreamSession.
 * The active workstream is URL state (`?ws=`, R3 — the rail writes it); a
 * draft (not-yet-created) chat is keyed by a client-minted id. Changing the
 * key remounts the session — that IS the reset (and, later, the switch):
 * opening an existing chat will hydrate its transcript from the server once
 * the DynamoDB-backed transcript endpoint exists; until then it opens empty.
 */
export function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeWsId = searchParams.get(WS_PARAM);
  // "+ New" mints a fresh draft id → key change → full session reset.
  const [draft, setDraft] = useState(() => ({ id: crypto.randomUUID(), openCreate: false }));
  // The workstream the CURRENT draft session created. While ?ws points at it,
  // keep the draft key — remounting would wipe the live transcript.
  const [createdId, setCreatedId] = useState<string | null>(null);
  // Navigating to a DIFFERENT workstream ends the draft session — drop the pin
  // (render-time state adjustment, guarded so it fires once), else clicking
  // back to the created workstream would resurrect the draft (and its modal)
  // instead of opening the workstream. Draft transcripts die on switch until
  // hydration lands (concept plan Stage 3f).
  if (createdId !== null && activeWsId !== null && activeWsId !== createdId) {
    setCreatedId(null);
  }
  const isOwnDraft = activeWsId === null || activeWsId === createdId;

  return (
    <WorkstreamSession
      key={isOwnDraft ? draft.id : activeWsId}
      initialWorkstreamId={isOwnDraft ? null : activeWsId}
      autoOpenCreate={isOwnDraft && draft.openCreate}
      onNew={() => {
        setDraft({ id: crypto.randomUUID(), openCreate: true });
        setCreatedId(null);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(WS_PARAM);
            return next;
          },
          { replace: true },
        );
      }}
      onCreated={(id) => {
        setCreatedId(id);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(WS_PARAM, id);
            return next;
          },
          { replace: true },
        );
      }}
    />
  );
}
HomePage.displayName = 'HomePage';
